import { getAuth } from 'firebase/auth';
import { apiUrl } from '../config/api';

async function getHeaders(): Promise<HeadersInit> {
  const auth = getAuth();
  const user = auth.currentUser;
  const token = user ? await user.getIdToken() : null;

  return {
    'Content-Type': 'application/json',
    ...(token && { Authorization: `Bearer ${token}` }),
  };
}

// Re-export type from shared — single source of truth
import type { HealthLogType as _HealthLogType, HealthLogEntry as _HealthLogEntry } from '@researcher/shared';
export type HealthLogType = _HealthLogType;
export type HealthLogEntry = _HealthLogEntry;

export interface HealthLogsPage {
  items: HealthLogEntry[];
  nextCursor: string | null;
  hasMore: boolean;
}

export async function getHealthLogs(params?: {
  startDate?: string;
  endDate?: string;
  limit?: number;
  type?: HealthLogType;
  cursor?: string;
}): Promise<HealthLogsPage> {
  const headers = await getHeaders();
  const searchParams = new URLSearchParams();
  if (params?.startDate) searchParams.set('startDate', params.startDate);
  if (params?.endDate) searchParams.set('endDate', params.endDate);
  if (params?.limit) searchParams.set('limit', String(params.limit));
  if (params?.type) searchParams.set('type', params.type);
  if (params?.cursor) searchParams.set('cursor', params.cursor);

  const url = apiUrl(`/health/logs?${searchParams.toString()}`);
  const response = await fetch(url, { headers });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.error || 'Failed to fetch health logs');
  }

  const data = await response.json();
  const items = (data.data || []).map((entry: any) => ({
    ...entry,
    type: entry.type || 'health', // backward compat
    // symptoms/medications are non-optional on HealthLogEntry, but some writers
    // (e.g. meal-plan log-meal) historically omitted them. Default to [] so the
    // type holds and consumers can safely .map/.filter/.length without guards.
    symptoms: entry.symptoms ?? [],
    medications: entry.medications ?? [],
  }));

  return {
    items,
    nextCursor: data.nextCursor || null,
    hasMore: data.hasMore ?? false,
  };
}

export async function deleteHealthLog(logId: string): Promise<void> {
  const headers = await getHeaders();
  const response = await fetch(apiUrl(`/health/logs/${logId}`), {
    method: 'DELETE',
    headers,
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.error || 'Failed to delete health log');
  }
}

export async function updateHealthLog(logId: string, entry: Partial<Omit<HealthLogEntry, 'id' | 'userId' | 'source' | 'createdAt' | 'updatedAt'>> & { date: string; type: HealthLogType }): Promise<HealthLogEntry> {
  const headers = await getHeaders();
  const response = await fetch(apiUrl(`/health/logs/${logId}`), {
    method: 'PUT',
    headers,
    body: JSON.stringify(entry),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.error || 'Failed to update health log');
  }

  const data = await response.json();
  return data.data;
}

export async function createHealthLog(entry: Partial<Omit<HealthLogEntry, 'id' | 'userId' | 'source' | 'createdAt' | 'updatedAt'>> & { date: string; type: HealthLogType }): Promise<HealthLogEntry> {
  const headers = await getHeaders();
  const response = await fetch(apiUrl('/health/logs'), {
    method: 'POST',
    headers,
    body: JSON.stringify(entry),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.error || 'Failed to create health log');
  }

  const data = await response.json();
  return data.data;
}

/** Create period bleeding entries for a date range (single API call). */
export async function createPeriodEntries(
  startDate: string,
  endDate: string,
  flowLevel: 'light' | 'medium' | 'heavy' = 'medium',
): Promise<{ created: number; skipped: number; dates: string[] }> {
  const headers = await getHeaders();
  const response = await fetch(apiUrl('/health/logs/period'), {
    method: 'POST',
    headers,
    body: JSON.stringify({ startDate, endDate, flowLevel }),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.error || 'Failed to create period entries');
  }

  const data = await response.json();
  return data.data;
}

export async function getHealthSummary(
  startDate: string,
  endDate: string,
  includeCycleData: boolean = false
): Promise<string> {
  const headers = await getHeaders();
  const response = await fetch(apiUrl('/health/summary'), {
    method: 'POST',
    headers,
    body: JSON.stringify({ startDate, endDate, includeCycleData }),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.error || 'Failed to generate health summary');
  }

  const data = await response.json();
  return data.summary;
}

